import "simplebar-react/dist/simplebar.min.css";
import { useEffect, useRef, useState } from "react";
import { floatTo16BitPCM } from "units/chatbot/utils/utils";
import { useSelector } from "react-redux";
import SimpleBar from "simplebar-react";
import ChatMessage from "./ChatMessage";
import { connectWebSocket } from "../services/websocketService";
import {
  chatbotMessages,
  chatbotSendMessage,
  createChatbotRoom
} from "../services/clientChatBotService";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaMicrophone, FaStop } from "react-icons/fa";
import { LuSend } from "react-icons/lu";
import { Bot } from "lucide-react";
import "./styles/clientChat.responsive.css";
import config from "../../../config.js";
import { useTranslation } from "react-i18next";

const ClientChatBox = ({
  disable,
  visible,
  selectedConversation,
  partnerId,
  countryCode,
  phone,
  chatToAdmin,
  botType,
  withGPT,
  accessToken,
  username,
  name,
  toggleView,
  isBotActive,
  welcomeMessage,
  isRoomLoading,
  onRoomCreated
}) => {
  const { t, i18n } = useTranslation(["client", "common"]);

  const [isRecording, setIsRecording] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [ws, setWs] = useState(null);
  const [buffer, setBuffer] = useState([]);
  const [messages, setMessages] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [userMessage, setUserMessage] = useState("");
  const [currentCategory, setCurrentCategory] = useState(botType);
  const [disableSend, setDisableSend] = useState(false);
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);

  const isEnglish = (i18n.language === "en");
  const dataIntervalId = useRef(null);
  const inputRef = useRef(null);
  const skipNextLoadRef = useRef(false);

  const navigate = useNavigate();
  const BASE_URL = config.cdnUrl;
  const logo = JSON.parse(localStorage.getItem("logo") || "null");
  const user = useSelector((state) => state.userInfo);
  const userId = user?.userId;
  const MAX_ROWS = 10;
  const LINE_HEIGHT = 24;
  const BASE_HEIGHT = 44;
  const isRtlLanguage = ["fa", "ar"].includes(i18n.language);
  const textareaDir = isRtlLanguage ? "rtl" : "ltr";
  const defaultWelcomeMessage = `سلام. به ${name?.trim() || username || ""
    } خوش آمدید. چه طور می تونم کمکتون کنم؟`;

  const initialWelcomeMessage = welcomeMessage?.trim() || defaultWelcomeMessage;
  const isComposerLocked = disableSend || isStreaming;
  /* -------------------------------------------------------------------------- */
  /*                              Browser Back                                  */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    window.history.pushState({ chatBox: true }, "", window.location.href);
    const handleBack = () => toggleView();
    window.addEventListener("popstate", handleBack);
    return () => window.removeEventListener("popstate", handleBack);
  }, [toggleView]);

  /* -------------------------------------------------------------------------- */
  /*                              Load Messages                                  */
  /* -------------------------------------------------------------------------- */

  const loadMessages = async () => {
    if (!selectedConversation?.id) {
      setMessages([]);
      return;
    }

    if (skipNextLoadRef.current) {
      skipNextLoadRef.current = false;
      return;
    }

    setIsHistoryLoading(true);

    try {
      const data = await chatbotMessages({
        accessToken,
        roomId: selectedConversation.id,
        username,
      });

      if (data?.ok) {
        const formattedMessages = data?.data?.map((msg) => {
          // 👈 جدید — استخراج suggestions از تاریخچه
          const responseSuggestions = Array.isArray(msg?.response?.suggestions)
            ? msg.response.suggestions
            : Array.isArray(msg?.suggestions)
              ? msg.suggestions
              : [];

          const suggestionsConsumed =
            msg?.suggestions_consumed ??
            msg?.response?.suggestions_consumed ??
            msg?.response?.consumed ??
            false;

          return {
            message: msg?.message,
            is_sent_by_user: msg?.senderId === userId,
            response: msg?.response?.message,
            resCreatedAt: msg?.response?.createdAt,
            createdAt: msg?.createdAt,
            status: msg?.response?.likeStatus,
            responseId: msg?.responseId,
            isNew: false,
            suggestions: responseSuggestions, // 👈 جدید
            suggestions_consumed: suggestionsConsumed, // 👈 جدید
          };
        });

        formattedMessages.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );

        setMessages(formattedMessages);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
  }, [selectedConversation]);

  /* -------------------------------------------------------------------------- */
  /*                              Reset Chat                                    */
  /* -------------------------------------------------------------------------- */

  const resetChatBoxState = () => {
    setIsRecording(false);
    setWs(null);
    setBuffer([]);
    setMessages([]);
    setUserMessage("");
    setCurrentCategory(null);

    if (dataIntervalId.current) {
      clearInterval(dataIntervalId.current);
      dataIntervalId.current = null;
    }
  };

  useEffect(() => {
    if (!selectedConversation) {
      resetChatBoxState();
    }
  }, [selectedConversation]);

  /* -------------------------------------------------------------------------- */
  /*                              Voice                                          */
  /* -------------------------------------------------------------------------- */

  const wsSender = () => {
    dataIntervalId.current = setInterval(() => {
      if (!isRecording || !ws || ws.readyState !== WebSocket.OPEN) {
        clearInterval(dataIntervalId.current);
        return;
      }
      const audioData = buffer.splice(0, 640);
      if (audioData.length > 0) {
        ws.send(floatTo16BitPCM(audioData));
      }
    }, 20);
  };

  const handleMicToggle = (isActive) => {
    if (!isActive) {
      clearInterval(dataIntervalId.current);
      if (ws && ws.readyState === WebSocket.OPEN) ws.close();
      setBuffer([]);
      setWs(null);
    } else {
      const newWebSocket = connectWebSocket(
        new Date().getTime(),
        new Date().getTime()
      );
      setWs(newWebSocket);

      newWebSocket.onopen = () => console.log("Reconnected to server successfully.");
      newWebSocket.onerror = (error) => console.error("WebSocket error:", error);
      newWebSocket.onclose = () => console.log("WebSocket connection closed.");

      newWebSocket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.predicted_txt) setUserMessage(data.predicted_txt);
        } catch (error) {
          console.error("Failed to parse JSON:", error);
        }
      };
    }

    setIsRecording(isActive);
  };

  /* -------------------------------------------------------------------------- */
  /*                              Send Message                                   */
  /* -------------------------------------------------------------------------- */

  const handleSendMessage = async (messageOverride = null, suggestionResponseId = null) => {
    if (!username || username === "undefined") return;

    const messageToSend =
      typeof messageOverride === "string" ? messageOverride : userMessage;

    if (!messageToSend.trim()) return;

    const messageText = messageToSend.trim();
    let roomId = selectedConversation?.id;

    const newMessage = {
      message: messageText,
      is_sent_by_user: true,
      response: "",
      responseId: null,
      status: null,
      isNew: false,
      suggestions: [],
      suggestions_consumed: true,
    };

    // 👈 جدید — اگه از کلیک روی پیشنهاد اومده، همون پیشنهادها رو optimistic مصرف‌شده کن
    if (suggestionResponseId) {
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.responseId === suggestionResponseId
            ? { ...msg, suggestions_consumed: true }
            : msg
        )
      );
    }

    setMessages((prevMessages) => [...prevMessages, newMessage]);
    setUserMessage("");
    setDisableSend(true);

    try {
      if (!roomId) {
        setIsCreatingRoom(true);

        const newRoom = await createChatbotRoom({ accessToken, username });

        setIsCreatingRoom(false);

        if (newRoom?.ok && newRoom?.data?.id) {
          roomId = newRoom.data.id;
          skipNextLoadRef.current = true;
          onRoomCreated?.(newRoom.data);
        } else {
          throw { message: t("chatbox.sendError") };
        }
      }

      const data = await chatbotSendMessage({
        accessToken,
        username,
        message: messageText,
        roomId,
      });
      console.log("data---> ", data);
      if (data?.ok) {
        const debugMessage = data?.data?.debug;
        const botResponse = data?.data?.response?.message || "";
        const respId = data?.data?.response?.id || "";
        const createdAt = data?.data?.createdAt;
        const status = data?.data?.likeStatus || null;

        // 👈 جدید — استخراج suggestions از جواب سرور
        const responseSuggestions = Array.isArray(data?.data?.response?.suggestions)
          ? data.data.response.suggestions
          : Array.isArray(data?.data?.suggestions)
            ? data.data.suggestions
            : [];

        const suggestionsConsumed =
          data?.data?.suggestions_consumed ??
          data?.data?.response?.suggestions_consumed ??
          data?.data?.response?.consumed ??
          false;

        setMessages((prevMessages) => {
          const newMessages = [...prevMessages];

          const lastUserIndex = [...newMessages]
            .reverse()
            .findIndex((msg) => msg.is_sent_by_user && msg.response === "");

          const indexToUpdate =
            lastUserIndex !== -1 ? newMessages.length - 1 - lastUserIndex : -1;

          if (indexToUpdate !== -1) {
            newMessages[indexToUpdate] = {
              ...newMessages[indexToUpdate],
              response: botResponse,
              debug: debugMessage,
              createdAt,
              status,
              responseId: respId,
              isNew: true,
              suggestions: responseSuggestions, // 👈 جدید
              suggestions_consumed: suggestionsConsumed, // 👈 جدید
            };
          }

          return newMessages;
        });
      }
    } catch (error) {
      console.error("خطا در ارسال پیام:", error);
      toast.error(error.message || "خطای ناشناخته");

      // 👈 جدید — اگه ارسال از روی پیشنهاد بود و شکست خورد، برش‌گردون
      if (suggestionResponseId) {
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg.responseId === suggestionResponseId
              ? { ...msg, suggestions_consumed: false }
              : msg
          )
        );
      }

      if (error.redirect) {
        setTimeout(() => navigate(error.redirect), 1500);
      }
    } finally {
      window.dispatchEvent(new Event("walletUpdated"));
      setDisableSend(false);
      setIsCreatingRoom(false);
    }
  };

  // 👈 جدید — کلیک روی یکی از دکمه‌های پیشنهاد
  const handleSuggestionClick = (suggestion, responseId) => {
    if (!suggestion || isComposerLocked || !isBotActive) return;
    handleSendMessage(suggestion, responseId);
  };

  /* -------------------------------------------------------------------------- */
  /*                              WebSocket                                      */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    const websocket = connectWebSocket(
      new Date().getTime(),
      new Date().getTime()
    );

    websocket.onopen = () => console.log("Connected to server successfully.");
    websocket.onerror = (error) => console.error("WebSocket error:", error);
    websocket.onclose = () => console.log("WebSocket connection closed.");

    websocket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.predicted_txt) setUserMessage(data.predicted_txt);
      } catch (error) {
        console.error("Failed to parse JSON:", error);
      }
    };

    setWs(websocket);

    return () => {
      if (websocket.readyState === WebSocket.OPEN) websocket.close();
    };
  }, []);

  useEffect(() => {
    if (isRecording && buffer.length > 0) wsSender();
    return () => clearInterval(dataIntervalId.current);
  }, [buffer, isRecording]);

  /* -------------------------------------------------------------------------- */
  /*                              Send State                                     */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];

    if (lastMessage?.message === "" && lastMessage?.response) {
      setDisableSend(false);
    } else if (lastMessage?.is_sent_by_user && !lastMessage?.response) {
      setDisableSend(true);
    }
  }, [messages]);

  /* -------------------------------------------------------------------------- */
  /*                              Textarea                                       */
  /* -------------------------------------------------------------------------- */

  const handleInputChange = (e) => {
    const textarea = e.target;
    const value = textarea.value;

    setUserMessage(value);
    textarea.style.height = "auto";

    if (!value.trim()) {
      textarea.style.height = `${BASE_HEIGHT}px`;
      textarea.style.overflowY = "hidden";
      return;
    }

    const maxHeight = MAX_ROWS * LINE_HEIGHT;
    const scrollHeight = textarea.scrollHeight;

    if (scrollHeight > maxHeight) {
      textarea.style.height = `${maxHeight}px`;
      textarea.style.overflowY = "auto";
    } else {
      textarea.style.height = `${scrollHeight}px`;
      textarea.style.overflowY = "hidden";
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();

      if (!isComposerLocked && isBotActive && userMessage.trim()) {
        handleSendMessage();
      }

      e.target.style.height = `${BASE_HEIGHT}px`;
      e.target.style.overflowY = "hidden";
    }
  };

  const userPhone = `${countryCode?.replace("+", "") || ""}${phone || ""}`;
  const canSend = !isComposerLocked && isBotActive && userMessage.trim();
  const isEmpty = !isHistoryLoading && messages.length === 0;

  return (
    <div
      className="client-chat-shell h-full relative flex flex-col w-full overflow-hidden !bg-white dark:!bg-zinc-900"
    >
      {/* Messages / Empty state */}
      <div className="flex-1 min-h-0 !bg-gradient-to-br !from-[#eaf6f4] !to-white dark:!bg-gradient-to-br dark:!from-[#121818] dark:!to-[#18181b]">
        {isEmpty ? (
          <div className="chat-empty-state h-full flex items-center justify-center px-6">
            <div className="text-center max-w-md">
              {logo ? (
                <div className="relative chat-empty-icon w-24 h-24 mx-auto mb-4">
                  <span className="absolute -inset-2 rounded-full bg-gradient-to-br from-teal-500/30 to-amber-400/20 blur-xl pointer-events-none -z-10 animate-pulse" />
                  <span className="absolute inset-0 rounded-full bg-teal-400/30 animate-ping" />
                  <img
                    src={`${BASE_URL}${logo}`}
                    alt="خوش‌آمد"
                    className="relative w-full h-full object-cover rounded-full ring-2 ring-teal-400/40 shadow-lg shadow-teal-500/30"
                  />
                </div>
              ) : (
                <div className="relative chat-empty-icon w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-teal-400 via-teal-500 to-amber-400 flex items-center justify-center text-white shadow-lg shadow-teal-500/40 ring-1 ring-teal-300/40">
                  <Bot className="w-12 h-12 relative z-10" />
                  <span className="absolute -inset-2 rounded-full bg-gradient-to-br from-teal-500/30 to-amber-400/20 blur-xl pointer-events-none -z-10 animate-pulse" />
                  <span className="absolute inset-0 rounded-full bg-teal-400/30 animate-ping" />
                </div>
              )}

              <p className={`chat-empty-text !text-gray-500 dark:!text-gray-400 leading-8 ${isEnglish && "font-sans"}`}>
                {initialWelcomeMessage}
              </p>
            </div>
          </div>
        ) : (
          <div className="client-chat-messages-inner mt-10 w-full max-w-3xl lg:max-w-[90%] mx-auto h-full">
            <ChatMessage
              messages={messages}
              chatLoading={chatLoading}
              roomId={selectedConversation?.id}
              onStreamingChange={setIsStreaming}
              onSuggestionClick={handleSuggestionClick} // 👈 جدید
              suggestionsDisabled={isComposerLocked || !isBotActive} // 👈 جدید
            />
          </div>
        )}
      </div>

      {/* Composer */}
      {!disable && (
        <div
          id="chat-input"
          className="client-chat-composer flex-shrink-0 !bg-white dark:!bg-zinc-900"
          dir="rtl"
        >
          <div className="max-w-3xl mx-auto lg:max-w-[50%] w-full">
            <div
              className={`
        relative flex items-end gap-1.5 w-full rounded-2xl border px-2 py-1.5
        transition-all duration-300 ease-out
        ${isBotActive
                  ? "!border-gray-200 dark:!border-teal-800/50 !bg-gradient-to-br !from-teal-50/60 !via-gray-100 dark:!from-teal-950/30 dark:!via-zinc-800 dark:!to-cyan-950/20 focus-within:border-teal-500 focus-within:ring-2 focus-within:ring-teal-500/30 focus-within:shadow-[0_0_20px_rgba(20,184,166,0.35)]"
                  : "!border-gray-200 dark:!border-zinc-700 !bg-gray-100 dark:!bg-zinc-800"
                }
      `}
            >
              <button
                type="button"
                disabled={!canSend}
                onClick={handleSendMessage}
                aria-label="ارسال پیام"
                className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center mb-0.5 transition-all duration-200 ${canSend
                  ? "bg-gradient-to-br from-teal-500 via-teal-600 to-cyan-700 text-white shadow-md shadow-teal-500/40 ring-1 ring-teal-300/40 hover:shadow-lg hover:shadow-teal-500/50 hover:-translate-y-0.5 hover:ring-teal-200/60 active:scale-95 active:translate-y-0"
                  : "!bg-gray-200 dark:!bg-zinc-700 !text-gray-400 dark:!text-zinc-500 cursor-not-allowed"
                  }`}
              >
                <LuSend className="w-[17px] h-[17px]" />
              </button>

              <textarea
                ref={inputRef}
                disabled={isComposerLocked || !isBotActive}
                value={userMessage}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                dir={textareaDir}
                placeholder={
                  isBotActive
                    ? t("chatbox.placeholder")
                    : t("chatbox.placeholderInactive")
                }
                rows={1}
                style={{ height: BASE_HEIGHT }}
                className={`flex-1 min-w-0 bg-transparent border-0 outline-none resize-none py-2 px-2 leading-6 text-sm text-gray-800 dark:!text-gray-100 placeholder:text-gray-400 dark:!placeholder:text-zinc-500 placeholder:text-auto focus:ring-0 ${!isBotActive ? "cursor-not-allowed" : ""
                  }`}
              />
            </div>

            {!disableSend && isBotActive && (
              <div className="client-chat-helper-text text-center text-[10px] !text-gray-400 dark:!text-zinc-600 mt-1.5">
                {t("chatbox.helperText")}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientChatBox;